import { CrewWorkDay, CrewWorkEmployee, CrewWorkJob, CrewWorkPunch } from "../interfaces/CrewWork";

/*
 * Pure filtering for the Crew Work supervisor list and crew detail.
 *
 * Same conventions as projectWorkFilter.ts: lowercase, trimmed, plain substring over one
 * flat searchable string per row, fields joined by a separator so a term can't bridge two
 * of them. A name/ID hit keeps the whole employee ("direct"); a hit only on a job label,
 * account name or PO # keeps just what matched and opens the row ("deep").
 *
 * Scope chips, the job picker and the account picker narrow the punches themselves, so the
 * day / job totals of a narrowed view are recomputed from what is shown — the page labels
 * them with `crewScopeNote`. The week KPIs are never filtered.
 */

const SEP = " · ";

const searchable = (...parts: (string | null | undefined)[]): string =>
    parts.filter((p) => !!p).join(SEP).toLowerCase();

export const normalizeTerm = (term: string): string => term.trim().toLowerCase();

/** Scope chips: every punch, open punches only, or edited / hand-entered punches only. */
export type CrewScope = "all" | "open" | "flagged";

// ── Supervisor list ─────────────────────────────────────────────────────────

export interface SupervisorListFilterResult<T> {
    supervisors: T[];
    matches: number;
}

/** `term` must already be normalized. Matches supervisor name or employee #. */
export const filterSupervisorList = <T extends { name: string; altEmployeeID: string }>(
    supervisors: T[],
    term: string
): SupervisorListFilterResult<T> => {
    if (!term) return { supervisors, matches: 0 };
    const kept = supervisors.filter((s) => searchable(s.name, s.altEmployeeID).includes(term));
    return { supervisors: kept, matches: kept.length };
};

// ── Crew accordion ──────────────────────────────────────────────────────────

export interface FilteredCrewEmployee {
    emp: CrewWorkEmployee;
    /** Name or ID matched: the row shows whole (within the chip / job scope). */
    direct: boolean;
    /** Only a job / account / PO matched: the row auto-expands, trimmed to the hit. */
    deepOnly: boolean;
    /** Jobs in view, with totals recomputed when anything narrowed them. */
    jobs: CrewWorkJob[];
}

export interface CrewFilterResult {
    rows: FilteredCrewEmployee[];
    matches: number;
    /** Employees whose row must be open because the hit is inside it. */
    revealIDs: number[];
}

/** Closed meal length (the API's `minutes` is already 0 for an open punch). */
const mealLength = (p: CrewWorkPunch): number => (p.type === "meal" && !p.open ? p.minutes : 0);

const punchInScope = (p: CrewWorkPunch, scope: CrewScope): boolean => {
    if (scope === "open") return p.open;
    if (scope === "flagged") return p.edited || p.manual;
    return true;
};

/** Account picker key for a punch: the AccountCodeID, or 'none' for uncoded work. */
export const accountKeyOf = (p: CrewWorkPunch): string => (p.accountCodeID == null ? "none" : String(p.accountCodeID));

/**
 * The account picker is about work: a meal carries no account code, so it never matches a
 * picked account — not even the "No account code" bucket, which means uncoded WORK.
 */
const punchOnAccount = (p: CrewWorkPunch, accountKey: string | null): boolean =>
    accountKey == null || (p.type === "work" && accountKeyOf(p) === accountKey);

const rebuildDay = (day: CrewWorkDay, punches: CrewWorkPunch[]): CrewWorkDay =>
    punches.length === day.punches.length
        ? day
        : {
              ...day,
              punches,
              workedMinutes: punches.reduce((a, p) => a + (p.type === "work" ? p.workedMinutes : 0), 0),
              mealMinutes: punches.reduce((a, p) => a + mealLength(p), 0),
          };

const rebuildJob = (job: CrewWorkJob, days: CrewWorkDay[]): CrewWorkJob => {
    const unchanged = days.length === job.days.length && days.every((d, i) => d === job.days[i]);
    if (unchanged) return job;
    const punches = days.flatMap((d) => d.punches);
    return {
        ...job,
        days,
        workedMinutes: days.reduce((a, d) => a + d.workedMinutes, 0),
        punchCount: punches.length,
        openPunchCount: punches.filter((p) => p.open).length,
    };
};

/** Apply the chip, the job picker and the account picker; days and jobs left empty drop out. */
const scopeJobs = (
    jobs: CrewWorkJob[],
    scope: CrewScope,
    jobKey: string | null,
    accountKey: string | null
): CrewWorkJob[] =>
    jobs
        .filter((j) => jobKey == null || j.key === jobKey)
        .map((j) => {
            if (scope === "all" && accountKey == null) return j;
            const days = j.days
                .map((d) =>
                    rebuildDay(
                        d,
                        d.punches.filter((p) => punchInScope(p, scope) && punchOnAccount(p, accountKey))
                    )
                )
                .filter((d) => d.punches.length > 0);
            return rebuildJob(j, days);
        })
        .filter((j) => j.days.length > 0);

const empText = (e: CrewWorkEmployee) => searchable(e.name, e.altEmployeeID);
const jobText = (j: CrewWorkJob) => searchable(j.label, j.altProjectID, j.name);
const punchText = (p: CrewWorkPunch) => searchable(p.accountName, p.poNumber);

/**
 * Deep match inside one employee's (already scoped) jobs: a job whose label hits stays whole;
 * otherwise a day stays whole when any of its punches hits on account or PO — the rest of
 * that day (meals, other segments) is context for the hit, not noise.
 */
const deepJobs = (jobs: CrewWorkJob[], term: string): CrewWorkJob[] =>
    jobs
        .map((j) => {
            if (jobText(j).includes(term)) return j;
            const days = j.days.filter((d) => d.punches.some((p) => punchText(p).includes(term)));
            return rebuildJob(j, days);
        })
        .filter((j) => j.days.length > 0);

/**
 * `term` must already be normalized; `jobKey` / `accountKey` null mean every job / every
 * account. The chip and the two pickers narrow first, then the term filters within them. A
 * crew member with no time this week survives only when nothing narrows by punch (scope
 * `all`, no job, no account) — otherwise there is nothing of theirs to show.
 */
export const filterCrewEmployees = (
    employees: CrewWorkEmployee[],
    term: string,
    scope: CrewScope,
    jobKey: string | null,
    accountKey: string | null = null
): CrewFilterResult => {
    const out: CrewFilterResult = { rows: [], matches: 0, revealIDs: [] };
    const narrowsPunches = scope !== "all" || jobKey != null || accountKey != null;

    employees.forEach((emp) => {
        const allJobs = emp.jobs ?? [];
        const jobs = scopeJobs(allJobs, scope, jobKey, accountKey);
        if (jobs.length === 0 && (narrowsPunches || allJobs.length > 0)) return;

        const row: FilteredCrewEmployee = { emp, direct: false, deepOnly: false, jobs };

        if (!term) {
            out.rows.push(row);
            out.matches += 1;
            return;
        }

        // Searching a person means you want everything about that person.
        if (empText(emp).includes(term)) {
            out.rows.push({ ...row, direct: true });
            out.matches += 1;
            return;
        }

        const hits = deepJobs(jobs, term);
        if (hits.length === 0) return;
        out.revealIDs.push(emp.employeeID);
        out.matches += 1;
        out.rows.push({ ...row, deepOnly: true, jobs: hits });
    });

    return out;
};

/**
 * Inline note for a narrowed punch view, so a reduced total can't be misread. `accountLabel`
 * is the picked account's display name (undefined when every account is in view).
 */
export const crewScopeNote = (scope: CrewScope, accountLabel?: string): string | undefined => {
    const parts: string[] = [];
    if (scope === "open") parts.push("open punches only");
    else if (scope === "flagged") parts.push("edited / manual punches only");
    if (accountLabel) parts.push(`${accountLabel} only`);
    return parts.length > 0 ? parts.join(", ") : undefined;
};

/** Punches that were hand-entered or corrected (a punch that is both counts once). */
export const flaggedPunchCount = (emp: CrewWorkEmployee): number =>
    (emp.jobs ?? []).reduce(
        (a, j) => a + j.days.reduce((b, d) => b + d.punches.filter((p) => p.edited || p.manual).length, 0),
        0
    );

// ── Job picker ──────────────────────────────────────────────────────────────

export interface CrewJobOption {
    key: string;
    label: string;
    /** Crew members with time on this job. */
    employeeCount: number;
}

type JobSortable = { projectID: number | null; altProjectID: string; label: string };

/** Same order as the API: job # numerically, then by label; "No Job" (projectID null) last. */
const compareJobs = (a: JobSortable, b: JobSortable): number => {
    const aNone = a.projectID == null;
    const bNone = b.projectID == null;
    if (aNone !== bNone) return aNone ? 1 : -1;
    const an = Number(a.altProjectID);
    const bn = Number(b.altProjectID);
    const aNum = a.altProjectID !== "" && Number.isFinite(an);
    const bNum = b.altProjectID !== "" && Number.isFinite(bn);
    if (aNum && bNum && an !== bn) return an - bn;
    if (aNum !== bNum) return aNum ? -1 : 1;
    return a.label.localeCompare(b.label);
};

/** Distinct jobs across the crew, for the Job picker. */
export const jobOptions = (employees: CrewWorkEmployee[]): CrewJobOption[] => {
    const byKey = new Map<string, CrewJobOption & JobSortable>();
    employees.forEach((e) =>
        (e.jobs ?? []).forEach((j) => {
            const cur = byKey.get(j.key);
            if (cur) cur.employeeCount += 1;
            else
                byKey.set(j.key, {
                    key: j.key,
                    label: j.label,
                    employeeCount: 1,
                    projectID: j.projectID,
                    altProjectID: j.altProjectID,
                });
        })
    );
    return Array.from(byKey.values())
        .sort(compareJobs)
        .map(({ key, label, employeeCount }) => ({ key, label, employeeCount }));
};

// ── Account picker ──────────────────────────────────────────────────────────

export const NO_ACCOUNT_LABEL = "No account code";

export interface CrewAccountOption {
    /** AccountCodeID as a string, or 'none' for uncoded work. */
    key: string;
    label: string;
    /** Crew members with work on this account code. */
    employeeCount: number;
}

/** Distinct account codes on WORK punches across the crew, by name; "No account code" last. */
export const accountOptions = (employees: CrewWorkEmployee[]): CrewAccountOption[] => {
    const byKey = new Map<string, CrewAccountOption>();
    employees.forEach((e) => {
        const seen = new Set<string>();
        (e.jobs ?? []).forEach((j) =>
            j.days.forEach((d) =>
                d.punches.forEach((p) => {
                    if (p.type !== "work") return;
                    const key = accountKeyOf(p);
                    if (seen.has(key)) return;
                    seen.add(key);
                    const cur = byKey.get(key);
                    if (cur) cur.employeeCount += 1;
                    else byKey.set(key, { key, label: key === "none" ? NO_ACCOUNT_LABEL : p.accountName || `Account ${key}`, employeeCount: 1 });
                })
            )
        );
    });
    return Array.from(byKey.values()).sort((a, b) => {
        if ((a.key === "none") !== (b.key === "none")) return a.key === "none" ? 1 : -1;
        return a.label.localeCompare(b.label);
    });
};
